/**
 * Committed LLM rate card — the single hand-reviewed source of truth for
 * model pricing, keyed by (provider, API model id).
 *
 * Both pricing tables derive from this card: the host `MODEL_REGISTRY`
 * (`./models.ts`, via `fromCard`) and the costs plugin's `SEED_RATES`
 * (`plugins/plugin-costs/src/server/pricing.service.ts`, via
 * `listPublishedRates`). Neither table may carry its own price literals for a
 * carded model — the gemini-2.5-flash incident (two tables, one model, two
 * prices) is exactly the failure mode this file removes.
 *
 * Numbers are USD per million tokens at published list price, verified
 * against each provider's own price page. Tiered providers use the
 * small-context tier by convention:
 *
 * - Anthropic: https://platform.claude.com/docs/en/about-claude/pricing.md
 *   (5-minute cache-write rate; the 1h tier is not modeled).
 * - OpenAI: https://developers.openai.com/api/docs/pricing (standard tier;
 *   `platform.openai.com/docs/pricing` redirects here, `openai.com/api/pricing`
 *   is bot-blocked). Tiered models use the small-context tier.
 * - Google: https://ai.google.dev/gemini-api/docs/pricing (paid tier,
 *   <=200k-token prompts; the >200k tier and per-hour cache storage are not
 *   modeled).
 * - xAI: https://docs.x.ai/docs/pricing (<200k-token tier).
 *
 * Cost rows snapshot whatever rate is in effect at write time, so a wrong
 * entry here is not self-correcting: it poisons history until a guarded
 * repair migration fixes it (see
 * `plugins/plugin-costs/src/server/migrations/0002_rate_card_repair.sql`).
 * A scheduled drift check (`pnpm check:rates`, `src/scripts/check-rates.ts`)
 * diffs this card against the published sources; rate changes only ever land
 * as reviewed diffs to this file. Full reference:
 * docs/internal/operations/model-rate-cards.md.
 */

import type { ChatProvider } from '@reflex/shared';

/**
 * One published rate. `null` cache fields are semantic, not missing data:
 *
 * - `cacheReadUsdPerMtok: null` — the provider publishes no cached-input
 *   price for this model (e.g. caching unsupported on gpt-5.5-pro).
 * - `cacheWriteUsdPerMtok: null` — cache writes bill as ordinary input.
 *   Anthropic (5-minute tier) and GPT-6 models publish a separate write
 *   rate at 1.25x input; other entries use `null` here.
 *
 * Consumers convert to their own semantics: the costs plugin's `computeUsd`
 * bills `null` cache tokens at the input rate, while the registry's
 * `ModelPricing` renders `null` as `0` ("not separately charged").
 */
export interface PublishedRate {
  /** USD per million input tokens. */
  inputUsdPerMtok: number;
  /** USD per million output tokens. */
  outputUsdPerMtok: number;
  /** USD per million cache-read tokens; null = no published cached price. */
  cacheReadUsdPerMtok: number | null;
  /** USD per million cache-write tokens; null = writes bill as input. */
  cacheWriteUsdPerMtok: number | null;
  /**
   * The provider no longer lists this model on its published price page.
   * The entry keeps the last published rate so historical cost rows still
   * price, and the drift check stops requiring a live source listing.
   * Never delete a delisted entry while cost history may reference it.
   */
  delisted?: true;
}

/**
 * The rate card. Only per-token-billed models belong here; dedicated
 * deployments billed per GPU (the GLM entries in `MODEL_REGISTRY`) have no
 * published per-token rate and stay zero-priced in the registry instead.
 */
export const RATE_CARD = {
  anthropic: {
    // Cache reads on Fable 5.1 are 0.025x input (a footnoted exception on
    // Anthropic's pricing page), not the 0.1x every other Anthropic model
    // uses — the multiplier invariant in `rate-card.test.ts` pins this
    // exception explicitly.
    'claude-fable-5-1': {
      inputUsdPerMtok: 10,
      outputUsdPerMtok: 50,
      cacheReadUsdPerMtok: 0.25,
      cacheWriteUsdPerMtok: 12.5,
    },
    'claude-fable-5': {
      inputUsdPerMtok: 10,
      outputUsdPerMtok: 50,
      cacheReadUsdPerMtok: 1.0,
      cacheWriteUsdPerMtok: 12.5,
    },
    // Cache reads on Opus 5.5 are 0.05x input (a footnoted exception on
    // Anthropic's pricing page, like Fable 5.1's 0.025x), not the standard
    // 0.1x — the multiplier invariant in `rate-card.test.ts` pins it.
    // Opus 5.5 is also the first Opus priced below the 5-generation line
    // ($4/$20 against $5/$25), so it does not inherit the shared Opus row.
    'claude-opus-5-5': {
      inputUsdPerMtok: 4,
      outputUsdPerMtok: 20,
      cacheReadUsdPerMtok: 0.2,
      cacheWriteUsdPerMtok: 5,
    },
    'claude-opus-5': {
      inputUsdPerMtok: 5,
      outputUsdPerMtok: 25,
      cacheReadUsdPerMtok: 0.5,
      cacheWriteUsdPerMtok: 6.25,
    },
    // The Opus 4.x line stays published at the 5-generation price. Seeding it
    // at the retired Opus 4.1 rate ($15/$75) once overstated every row ~3x.
    'claude-opus-4-8': {
      inputUsdPerMtok: 5,
      outputUsdPerMtok: 25,
      cacheReadUsdPerMtok: 0.5,
      cacheWriteUsdPerMtok: 6.25,
    },
    'claude-opus-4-7': {
      inputUsdPerMtok: 5,
      outputUsdPerMtok: 25,
      cacheReadUsdPerMtok: 0.5,
      cacheWriteUsdPerMtok: 6.25,
    },
    'claude-opus-4-6': {
      inputUsdPerMtok: 5,
      outputUsdPerMtok: 25,
      cacheReadUsdPerMtok: 0.5,
      cacheWriteUsdPerMtok: 6.25,
    },
    // Opus 4.5 and Sonnet 4.5 (below) are launchable from the Claude agent
    // picker but were never chat models, so they have no MODEL_REGISTRY
    // entry. Their dated build ids (`claude-opus-4-5-20251101`) price via
    // these undated keys through the costs seed lookup's dated-suffix
    // canonicalization.
    'claude-opus-4-5': {
      inputUsdPerMtok: 5,
      outputUsdPerMtok: 25,
      cacheReadUsdPerMtok: 0.5,
      cacheWriteUsdPerMtok: 6.25,
    },
    'claude-sonnet-5-5': {
      inputUsdPerMtok: 2,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.2,
      cacheWriteUsdPerMtok: 2.5,
    },
    'claude-sonnet-5': {
      inputUsdPerMtok: 2,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.2,
      cacheWriteUsdPerMtok: 2.5,
    },
    // Superseded by Sonnet 5 in the picker but still published: rows recorded
    // against Sonnet 4.6 bill at 4.6's rate, not the current Sonnet's.
    'claude-sonnet-4-6': {
      inputUsdPerMtok: 3,
      outputUsdPerMtok: 15,
      cacheReadUsdPerMtok: 0.3,
      cacheWriteUsdPerMtok: 3.75,
    },
    // See the Opus 4.5 note above — picker-only pin, priced via its dated id.
    'claude-sonnet-4-5': {
      inputUsdPerMtok: 3,
      outputUsdPerMtok: 15,
      cacheReadUsdPerMtok: 0.3,
      cacheWriteUsdPerMtok: 3.75,
    },
    'claude-haiku-4-5': {
      inputUsdPerMtok: 1,
      outputUsdPerMtok: 5,
      cacheReadUsdPerMtok: 0.1,
      cacheWriteUsdPerMtok: 1.25,
    },
  },
  openai: {
    // https://developers.openai.com/api/docs/models/gpt-6-astra (2026-09-04).
    'gpt-6-astra': {
      inputUsdPerMtok: 10,
      outputUsdPerMtok: 50,
      cacheReadUsdPerMtok: 1,
      cacheWriteUsdPerMtok: 12.5,
    },
    // https://developers.openai.com/api/docs/models/gpt-6.1-sol (2026-09-29).
    'gpt-6.1-sol': {
      inputUsdPerMtok: 2,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.1,
      cacheWriteUsdPerMtok: 2.5,
    },
    // https://openai.com/index/introducing-gpt-6-sol-and-luna/ (2026-09-22).
    'gpt-6-sol': {
      inputUsdPerMtok: 2,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.2,
      cacheWriteUsdPerMtok: 2.5,
    },
    'gpt-6-luna': {
      inputUsdPerMtok: 0.1,
      outputUsdPerMtok: 0.5,
      cacheReadUsdPerMtok: 0.01,
      cacheWriteUsdPerMtok: 0.125,
    },
    'gpt-5.6-sol': {
      inputUsdPerMtok: 4,
      outputUsdPerMtok: 20,
      cacheReadUsdPerMtok: 0.4,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5.6-terra': {
      inputUsdPerMtok: 2,
      outputUsdPerMtok: 12,
      cacheReadUsdPerMtok: 0.2,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5.6-luna': {
      inputUsdPerMtok: 0.2,
      outputUsdPerMtok: 1.2,
      cacheReadUsdPerMtok: 0.02,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5.5': {
      inputUsdPerMtok: 5,
      outputUsdPerMtok: 30,
      cacheReadUsdPerMtok: 0.5,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5.4-mini': {
      inputUsdPerMtok: 0.75,
      outputUsdPerMtok: 4.5,
      cacheReadUsdPerMtok: 0.075,
      cacheWriteUsdPerMtok: null,
    },
    // The pro tier publishes no cached-input price (caching unsupported).
    'gpt-5.5-pro': {
      inputUsdPerMtok: 30,
      outputUsdPerMtok: 180,
      cacheReadUsdPerMtok: null,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5.4': {
      inputUsdPerMtok: 2.5,
      outputUsdPerMtok: 15,
      cacheReadUsdPerMtok: 0.25,
      cacheWriteUsdPerMtok: null,
    },
    // Like the pro tier above, the older pro builds publish no cached-input
    // price (caching unsupported).
    'gpt-5.4-pro': {
      inputUsdPerMtok: 30,
      outputUsdPerMtok: 180,
      cacheReadUsdPerMtok: null,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5.2-pro': {
      inputUsdPerMtok: 21,
      outputUsdPerMtok: 168,
      cacheReadUsdPerMtok: null,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5-pro': {
      inputUsdPerMtok: 15,
      outputUsdPerMtok: 120,
      cacheReadUsdPerMtok: null,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5.2': {
      inputUsdPerMtok: 1.75,
      outputUsdPerMtok: 14,
      cacheReadUsdPerMtok: 0.175,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5.1': {
      inputUsdPerMtok: 1.25,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.125,
      cacheWriteUsdPerMtok: null,
    },
    // The vendored tokenlens snapshot also lists gpt-5, at a rounded $0.13
    // cache read — carding it makes the exact published $0.125 win.
    'gpt-5': {
      inputUsdPerMtok: 1.25,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.125,
      cacheWriteUsdPerMtok: null,
    },
    'gpt-5.3-codex': {
      inputUsdPerMtok: 1.75,
      outputUsdPerMtok: 14,
      cacheReadUsdPerMtok: 0.175,
      cacheWriteUsdPerMtok: null,
    },
    // The retired Codex line left the published price page but stays
    // launchable in the Codex agent picker, so rows kept recording — priced
    // at $0 until these entries landed (see the 0003 repair migration in
    // plugin-costs). Rates are the last published standard-tier prices,
    // verified against the 2025-12-15 capture of the OpenAI pricing page;
    // each Codex build billed at its base GPT generation's rate.
    'gpt-5.2-codex': {
      inputUsdPerMtok: 1.75,
      outputUsdPerMtok: 14,
      cacheReadUsdPerMtok: 0.175,
      cacheWriteUsdPerMtok: null,
      delisted: true,
    },
    'gpt-5.1-codex-max': {
      inputUsdPerMtok: 1.25,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.125,
      cacheWriteUsdPerMtok: null,
      delisted: true,
    },
    'gpt-5.1-codex': {
      inputUsdPerMtok: 1.25,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.125,
      cacheWriteUsdPerMtok: null,
      delisted: true,
    },
    'gpt-5.1-codex-mini': {
      inputUsdPerMtok: 0.25,
      outputUsdPerMtok: 2,
      cacheReadUsdPerMtok: 0.025,
      cacheWriteUsdPerMtok: null,
      delisted: true,
    },
    'gpt-5-codex': {
      inputUsdPerMtok: 1.25,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.125,
      cacheWriteUsdPerMtok: null,
      delisted: true,
    },
  },
  google: {
    'gemini-2.5-pro': {
      inputUsdPerMtok: 1.25,
      outputUsdPerMtok: 10,
      cacheReadUsdPerMtok: 0.125,
      cacheWriteUsdPerMtok: null,
    },
    'gemini-2.5-flash': {
      inputUsdPerMtok: 0.3,
      outputUsdPerMtok: 2.5,
      cacheReadUsdPerMtok: 0.03,
      cacheWriteUsdPerMtok: null,
    },
  },
  xai: {
    'grok-4.20-0309-non-reasoning': {
      inputUsdPerMtok: 1.25,
      outputUsdPerMtok: 2.5,
      cacheReadUsdPerMtok: 0.2,
      cacheWriteUsdPerMtok: null,
    },
    // Off docs.x.ai/docs/pricing and models.dev as of 2026-08-21; kept at its
    // last published rate so grok-4.1-fast rows keep pricing.
    'grok-4-1-fast-non-reasoning': {
      inputUsdPerMtok: 0.2,
      outputUsdPerMtok: 0.5,
      cacheReadUsdPerMtok: 0.05,
      cacheWriteUsdPerMtok: null,
      delisted: true,
    },
    // Superseded and off the published card; last published rate preserved
    // for historical grok-4 rows.
    'grok-4': {
      inputUsdPerMtok: 3,
      outputUsdPerMtok: 15,
      cacheReadUsdPerMtok: null,
      cacheWriteUsdPerMtok: null,
      delisted: true,
    },
  },
} satisfies Partial<Record<ChatProvider, Record<string, PublishedRate>>>;

export type RateCard = typeof RATE_CARD;
export type RateCardProvider = keyof RateCard;

/** A card entry flattened to a (provider, model) row. */
export interface PublishedModelRate extends PublishedRate {
  provider: RateCardProvider;
  model: string;
}

/**
 * Flatten the card into (provider, model, rate) rows — the shape the costs
 * plugin seeds from and the drift check diffs against. Delisted entries are
 * included: their whole point is that historical rows keep pricing.
 */
export function listPublishedRates(): readonly PublishedModelRate[] {
  const rows: PublishedModelRate[] = [];
  for (const [provider, models] of Object.entries(RATE_CARD)) {
    for (const [model, rate] of Object.entries<PublishedRate>(models)) {
      rows.push({ provider: provider as RateCardProvider, model, ...rate });
    }
  }
  return rows;
}
